import { useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { MdNotificationsActive, MdNotificationsOff } from 'react-icons/md';
import { IoShareOutline } from 'react-icons/io5';
import { MdAddToHomeScreen } from 'react-icons/md';
import usePushNotifications from '../Hooks/usePushNotifications';
import { useTheme } from '../context/ThemeContext';
import { DEFAULT_THEME } from '../Hooks/useTenantTheme';

export default function NotificationPrompt() {
  const { permission, isSupported, iosNeedsPWA, isBlocked, subscribe } =
    usePushNotifications();
  const themeContext = useTheme();
  const appName = themeContext?.theme?.appName || DEFAULT_THEME.appName;

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('notifPromptDismissed') === 'true'
  );

  const dismiss = () => {
    localStorage.setItem('notifPromptDismissed', 'true');
    setDismissed(true);
  };

  // ── iOS: not yet installed as PWA ────────────────────────────────────────
  if (iosNeedsPWA) {
    const installDismissed = localStorage.getItem('iosInstallDismissed') === 'true';
    if (installDismissed) return null;
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[99999] bg-canvas-primary text-white px-5 pt-4 pb-6 shadow-2xl rounded-t-2xl">
        <button
          onClick={() => {
            localStorage.setItem('iosInstallDismissed', 'true');
            // force re-render via a state update on the parent; here we rely on
            // the component re-rendering when the user navigates away.
            window.location.reload();
          }}
          className="absolute top-3 right-4 text-white opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          <RxCross2 className="w-5 h-5" />
        </button>

        <p className="font-outfit font-bold text-base mb-1">Install the {appName}</p>
        <p className="font-outfit text-sm opacity-80 mb-4">
          Add to your Home Screen to receive push notifications on iOS.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm font-bold">
              1
            </span>
            <span className="text-sm font-outfit">
              Tap the <IoShareOutline className="inline w-5 h-5 mb-0.5" />{' '}
              <strong>Share</strong> button in Safari's toolbar
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm font-bold">
              2
            </span>
            <span className="text-sm font-outfit">
              Scroll down and tap{' '}
              <MdAddToHomeScreen className="inline w-5 h-5 mb-0.5" />{' '}
              <strong>Add to Home Screen</strong>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm font-bold">
              3
            </span>
            <span className="text-sm font-outfit">
              Open the app from your Home Screen and allow notifications when prompted
            </span>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white opacity-60" />
        </div>
      </div>
    );
  }

  // ── Notifications blocked ────────────────────────────────────────────────
  if (isBlocked && !dismissed) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[99999] bg-red-50 border border-red-200 rounded-xl px-4 py-3 shadow-lg">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-red-400 hover:text-red-600"
          aria-label="Dismiss"
        >
          <RxCross2 className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-2 pr-5">
          <MdNotificationsOff className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm font-outfit">
            Notifications blocked — click the lock icon in your browser's address bar, set{' '}
            <strong>Notifications</strong> to <strong>Allow</strong>, then refresh.
          </p>
        </div>
      </div>
    );
  }

  // ── Already granted — nothing to show ───────────────────────────────────
  if (permission === 'granted') return null;

  // ── Default: prompt to subscribe ─────────────────────────────────────────
  if (!isSupported || dismissed || permission !== 'default') return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[99999] bg-white border border-gray-200 rounded-xl px-4 py-4 shadow-xl">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <RxCross2 className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 mb-3">
        <MdNotificationsActive className="w-6 h-6 text-canvas-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-outfit font-semibold text-gray-800 text-sm">Stay updated</p>
          <p className="font-outfit text-gray-500 text-xs mt-0.5">
            Enable notifications to receive reminders and canvassing updates.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={subscribe}
          className="flex-1 bg-canvas-primary text-white text-sm font-outfit font-semibold rounded-lg py-2 hover:opacity-90 transition-opacity"
        >
          Enable notifications
        </button>
        <button
          onClick={dismiss}
          className="px-4 text-sm font-outfit text-gray-500 hover:text-gray-700"
        >
          Later
        </button>
      </div>
    </div>
  );
}
